import { TMableEvent } from '@/core/constants/event_types/base_mable_event';
import { EMableEventName } from '@/core/constants/event_types/events';

// Custom event definition type
export type CustomEventDefinition = {
  baseEvent: EMableEventName;
  eventName: string;
  triggerCondition: {
    field: string | keyof TMableEvent; // Allow nested fields with dot notation
    operator:
      | 'contains'
      | 'endsWith'
      | 'equals'
      | 'greaterThan'
      | 'lessThan'
      | 'startsWith';
    value: any;
  };
};

// Helper function to get nested value from an object using dot notation
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

// Helper function to evaluate the trigger condition
function evaluateTriggerCondition(
  event: TMableEvent,
  condition: CustomEventDefinition['triggerCondition'],
): boolean {
  const value = getNestedValue(event, condition.field);

  switch (condition.operator) {
    case 'equals': {
      return value === condition.value;
    }
    case 'contains': {
      return typeof value === 'string' && value.includes(condition.value);
    }
    case 'startsWith': {
      return typeof value === 'string' && value.startsWith(condition.value);
    }
    case 'endsWith': {
      return typeof value === 'string' && value.endsWith(condition.value);
    }
    case 'greaterThan': {
      return typeof value === 'number' && value > condition.value;
    }
    case 'lessThan': {
      return typeof value === 'number' && value < condition.value;
    }
    default: {
      return false;
    }
  }
}

// Function to calculate custom events
export function calculateCustomEvents(
  mableEvent: TMableEvent,
  customEventDefinitions: CustomEventDefinition[],
): string[] {
  const triggeredEvents: string[] = [];

  for (const def of customEventDefinitions) {
    if (
      def &&
      mableEvent.en === def.baseEvent &&
      evaluateTriggerCondition(mableEvent, def.triggerCondition)
    ) {
      triggeredEvents.push(def.eventName);
    }
  }

  return triggeredEvents;
}

// Example usage
/* 
const customEventDefinitions: CustomEventDefinition[] = [
  {
    baseEvent: EMableEventName.PAGE_VIEWED,
    eventName: 'ProductPageView',
    triggerCondition: {
      field: 'pd.dl',
      operator: 'contains',
      value: '/products/'
    }
  },
  {
    baseEvent: EMableEventName.ORDER_COMPLETED,
    eventName: 'HighValuePurchase',
    triggerCondition: {
      field: 'esd.cv_data.cv',
      operator: 'greaterThan',
      value: 100
    }
  }
];

// Example Mable event (simplified for brevity)
const exampleMableEvent: TMableEvent = {
  en: EMableEventName.PAGE_VIEWED,
  pd: {
    dl: 'https://example.com/products/123',
    dt: 'Product Page',
    mp: 0,
    pb: 0,
    phct: 1,
    pp: 0,
    qpar: {},
    rl: ''
  },
  // ... other required properties
} as TMableEvent;

const triggeredEvents = calculateCustomEvents(exampleMableEvent, customEventDefinitions);
console.log('Triggered custom events:', triggeredEvents);
*/
